import type { IPlayerModel } from '@ai-content-hub/h5p-server';

import { mergeH5PIntegration, removeUnusedContent } from './h5p-utils';
import { addScripts, addStylesheets } from './dom-utils';
import { IH5P, IH5PInstance } from './h5p-types';
import { H5P_THEME_OVERRIDES_CSS } from './h5pThemeOverrides';

export interface IxAPIEvent {
    data: {
        statement: any;
    };
}

export interface IContext {
    contentId: string;
}

/**
 * A Web Component displaying H5P content.
 */
/**
 * EAA-1929 — one H5P may bootstrap at a time, page-wide.
 *
 * In div mode every player on the page shares ONE window: `window.H5P`,
 * `window.H5PIntegration` and, critically, the single global `H5P.preventInit`
 * flag that gates H5P's own bootstrap. Two players rendering concurrently
 * interleave "merge my integration → clear preventInit → H5P.init(my root)", so
 * one player's `init` can pick up another's `.h5p-content` element and bind it to
 * the wrong content — observed live as activity 1 rendering with activity 3's
 * dropdown options, and as duplicate entries in `H5P.instances`.
 *
 * Serialising the critical section costs nothing visible (each bootstrap is
 * milliseconds once the assets are cached) and removes the whole class of bug
 * without moving content into iframes, which collapses the host's height.
 */
let h5pBootstrapChain: Promise<void> = Promise.resolve();

function withH5PBootstrapLock<T>(work: () => Promise<T>): Promise<T> {
    const run = h5pBootstrapChain.then(work, work);
    // Keep the chain alive even if this bootstrap rejects.
    h5pBootstrapChain = run.then(
        () => undefined,
        () => undefined,
    );
    return run;
}

export class H5PPlayerComponent extends HTMLElement {
    get contentId(): string {
        return this.getAttribute('content-id');
    }

    set contentId(contentId: string) {
        this.setAttribute('content-id', contentId);
    }

    get contextId(): string {
        return this.getAttribute('context-id');
    }

    set contextId(contextId: string) {
        this.setAttribute('context-id', contextId);
    }

    get asUserId(): string {
        return this.getAttribute('as-user-id');
    }

    set asUserId(asUserId: string) {
        this.setAttribute('as-user-id', asUserId);
    }

    get readOnlyState(): string {
        return this.getAttribute('read-only-state');
    }

    set readOnlyState(readOnlyState: string) {
        this.setAttribute('read-only-state', readOnlyState);
    }

    /**
     * The internal H5P instance object of the H5P content.
     *
     * Only available after the `initialized` event was fired. Important: This
     * object is only partially typed and there are more properties and methods
     * on it!
     */
    get h5pInstance(): IH5PInstance {
        return this.h5pInstanceInternal;
    }
    private set h5pInstance(value: IH5PInstance) {
        this.h5pInstanceInternal = value;
    }

    /**
     * The global H5P object / namespace (normally accessible through "H5P..."
     * or "window.H5P") of the content type. Depending on the embed type this
     * can be an object from the internal iframe, so you can use it to break the
     * barrier of the iframe and execute JavaScript inside the iframe.
     *
     * Only available after the `initialized` event was fired. Important: This
     * object is only partially typed and there are more properties and methods
     * on it!
     */
    get h5pObject(): IH5P {
        return this.h5pObjectInternal;
    }
    private set h5pObject(value: IH5P) {
        this.h5pObjectInternal = value;
    }

    /**
     * The window object in which the H5P object exists and is rendered in. This
     * is the iframe's contentWindow or the parent's window, depending on the
     * embed type.
     */
    get h5pWindow(): any {
        return this.h5pWindowInternal;
    }
    private set h5pWindow(value: any) {
        this.h5pWindowInternal = value;
    }

    /**
     * Called when the component needs to load data about content. The endpoint
     * called in here should call H5PPlayer.render() and send back the player
     * model.
     *
     * Should throw an error with a message in the message property if something
     * goes wrong.
     */
    public get loadContentCallback(): (
        contentId: string,
        contextId?: string,
        asUserId?: string,
        readOnlyState?: boolean
    ) => Promise<IPlayerModel> {
        return this.privateLoadContentCallback;
    }

    public set loadContentCallback(
        callback: (
            contentId: string,
            contextId?: string,
            asUserId?: string,
            readOnlyState?: boolean
        ) => Promise<IPlayerModel>
    ) {
        const mustRender = this.privateLoadContentCallback !== callback;
        this.privateLoadContentCallback = callback;
        if (mustRender) {
            this.scheduleRender();
        }
    }

    /**
     * EAA-755: coalesce the render triggers that fire in the same tick.
     *
     * TWO independent paths ask for a render, and React trips both on a single
     * commit: setting the `content-id` ATTRIBUTE (-> attributeChangedCallback) and
     * assigning the `loadContentCallback` PROPERTY (-> the setter above, which
     * compares by identity, so an unmemoized callback re-triggers on every parent
     * re-render). Each call ran a full bootstrap, so one mount produced TWO
     * `.h5p-content` elements stacked under one root and two `H5P.instances`
     * entries for one contentId — the activity rendered twice.
     *
     * `renderGeneration` alone could not prevent it: it is checked before the long
     * awaits (content load, `addScripts`), so two renders could both pass the guard
     * and then both append. Collapsing a burst into ONE render on the microtask
     * queue fixes the cause instead of cleaning up after it — which also keeps
     * `H5P.instances` free of the stale duplicate that `tryEmitInitialized()` would
     * otherwise match first when it looks an instance up by contentId.
     */
    private scheduleRender(): void {
        if (this.renderScheduled) {
            return;
        }
        this.renderScheduled = true;
        queueMicrotask(() => {
            this.renderScheduled = false;
            if (!this.isConnected) {
                return;
            }
            void this.render(
                this.contentId,
                this.contextId,
                this.asUserId,
                this.readOnlyState,
            );
        });
    }

    /**
     * Indicates changes to which attributes should trigger calls to
     * attributeChangedCallback.
     * @memberof H5PPlayerComponent
     */
    static get observedAttributes(): string[] {
        return ['content-id', 'context-id', 'as-user-id', 'read-only-state'];
    }
    constructor() {
        super();

        H5PPlayerComponent.initTemplate();
    }

    private static template: HTMLTemplateElement;
    private playerModel: IPlayerModel;
    private privateLoadContentCallback: (
        contentId: string,
        contextId?: string,
        asUserId?: string,
        readOnlyState?: boolean
    ) => Promise<IPlayerModel>;
    private resizeObserver: ResizeObserver;
    private root: HTMLElement;
    private h5pInstanceInternal: IH5PInstance;
    private initializedEmitted = false;
    private initializedFallbackTimer: ReturnType<typeof setInterval> | null = null;
    /** EAA-1929: watches a same-origin H5P iframe so the host can follow its height. */
    private iframeHeightObserver: ResizeObserver | null = null;

    /**
     * EAA-755: the host tokens `overrides/ach-player.css` aliases (its `:root` block).
     * Keep this list in step with that sheet — a token missing here simply falls back
     * to the sheet's own literal for the active mode, so the content stays legible but
     * stops following a tenant's palette.
     */
    private static readonly THEME_TOKENS = [
        '--foreground',
        '--card',
        '--primary',
        '--primary-foreground',
        '--muted',
        '--muted-foreground',
        '--background',
        '--accent',
        '--accent-foreground',
        '--success',
        '--destructive',
        '--warning',
        '--border',
        '--ring',
        '--radius',
    ] as const;

    /** EAA-755: set while a coalesced render is queued on the microtask queue. */
    private renderScheduled = false;

    /** EAA-755: mirrors the host's light/dark theme into a same-origin H5P iframe. */
    private iframeThemeObserver: MutationObserver | null = null;
    private iframeThemeTimer: ReturnType<typeof setInterval> | null = null;
    private iframeHeightTimer: ReturnType<typeof setInterval> | null = null;
    /**
     * EAA-1929-h5p-resume-draft-overwrite: short fallback poll for the isolated
     * iframe's OWN `window.H5P`, backing up the deterministic accessor installed by
     * `armIframeUserStatePatch` so `patchNewRunnableForEmbedUserState` still lands
     * if the accessor could not be installed.
     */
    private iframeUserStatePatchTimer: ReturnType<typeof setInterval> | null = null;
    private h5pObjectInternal: IH5P;
    private h5pWindowInternal: any;
    /**
     * Monotonic counter so overlapping `render()` calls (rapid attribute updates,
     * loadContentCallback reference changes) cannot finish out of order and run
     * `H5P.init` with a stale player model (e.g. before embed/session merge landed).
     */
    private renderGeneration = 0;

    private static initTemplate(): void {
        // We create the static template only once
        if (!H5PPlayerComponent.template) {
            H5PPlayerComponent.template = document.createElement('template');
            H5PPlayerComponent.template.innerHTML = `
                <style>
                    .h5p-iframe {
                        font-family: Sans-Serif;
                        width: 100%;
                        margin: 0;
                        padding: 0;
                    }
                    .h5p-iframe .h5p-container {
                        overflow: hidden;
                    }
                    .h5p-iframe .h5p-content {
                        font-size: 16px;
                        line-height: 1.5em;
                        width: 100%;
                        height: auto;
                    }
                    .h5p-iframe .h5p-fullscreen .h5p-content,
                    .h5p-fullscreen .h5p-iframe,
                    .h5p-iframe .h5p-semi-fullscreen .h5p-content {
                        height: 100%;
                    }
                    ${H5P_THEME_OVERRIDES_CSS}
                </style>
                <div class="h5p-player-component-root"></div>
                `;
        }
    }

    /**
     * Called when one of the attributes in observedAttributes changes.
     */
    async attributeChangedCallback(name: string, oldVal: any, _newVal: any): Promise<void> {
        if (!H5PPlayerComponent.observedAttributes.includes(name)) {
            return;
        }
        if (oldVal) {
            removeUnusedContent(name === 'content-id' ? oldVal : this.contentId);
        }
        // EAA-755: schedule instead of rendering per attribute. The callback fires
        // AFTER the attribute is written, so the getters below already return the new
        // values — and a commit that sets several of these at once now costs one
        // render, not one per attribute. See scheduleRender().
        this.scheduleRender();
    }

    /**
     * Called when the component is added to the DOM.
     */
    connectedCallback(): void {
        this.appendChild(H5PPlayerComponent.template.content.cloneNode(true));
        this.root = this.querySelector('.h5p-player-component-root');

        // We must notify the H5P content inside the player that the size of the
        // component has changed. Otherwise some content types won't resize
        // properly.
        this.resizeObserver = new ResizeObserver(() => {
            if (window.H5P?.instances) {
                window.H5P.instances.forEach((instance) => {
                    instance.trigger('resize');
                });
            }
        });
        this.resizeObserver.observe(this);
    }

    /**
     * Called when the component is removed from the DOM.
     */
    disconnectedCallback(): void {
        if (this.contentId) {
            removeUnusedContent(this.contentId);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        // EAA-1929: stop following the iframe height once this player goes away.
        if (this.iframeHeightObserver) {
            this.iframeHeightObserver.disconnect();
            this.iframeHeightObserver = null;
        }
        if (this.iframeHeightTimer) {
            clearInterval(this.iframeHeightTimer);
            this.iframeHeightTimer = null;
        }
        // EAA-755: stop mirroring the host theme once this player goes away.
        if (this.iframeThemeObserver) {
            this.iframeThemeObserver.disconnect();
            this.iframeThemeObserver = null;
        }
        if (this.iframeThemeTimer) {
            clearInterval(this.iframeThemeTimer);
            this.iframeThemeTimer = null;
        }
        // EAA-1929-h5p-resume-draft-overwrite: stop polling for the iframe's own
        // H5P global once this player goes away.
        if (this.iframeUserStatePatchTimer) {
            clearInterval(this.iframeUserStatePatchTimer);
            this.iframeUserStatePatchTimer = null;
        }
        this.clearInitializedFallback();
        if (window.H5P?.externalDispatcher) {
            window.H5P.externalDispatcher.off('initialized', this.onContentInitialized);
            window.H5P.externalDispatcher.off('xAPI', this.onxAPI);
        }
    }

    /**
     * Returns the copyright notice in HTML that you can insert somewhere to
     * display it. Undefined if there is no copyright information.
     */
    public getCopyrightHtml(): string | undefined {
        if (!this.h5pInstance) {
            console.error(
                'Cannot show copyright as H5P instance is undefined. The H5P object might not be initialized yet.'
            );
            return undefined;
        }
        if (!this.h5pObject) {
            console.error(
                'H5P object undefined. This typically means H5P has not been initialized yet.'
            );
            return undefined;
        }

        let metadata = this.h5pInstance.contentData?.metadata;
        if (!metadata) {
            metadata =
                this.playerModel.integration.contents[`cid-${this.playerModel.contentId}`].metadata;
            if (!metadata) {
                return undefined;
            }
        }

        let parameters: any;
        // We need to call JSON.parse in the context of the window the H5P
        // content exists in. The reason is that H5P.getCopyrights compares
        // object prototypes when traversing the parameters and the object
        // prototypes are not identical across windows.  (Cp.
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/instanceof#instanceof_and_multiple_context_e.g._frames_or_windows)
        try {
            parameters = this.h5pWindow.JSON.parse(
                this.playerModel.integration.contents[`cid-${this.playerModel.contentId}`]
                    .jsonContent
            );
        } catch (error: any) {
            console.error(
                'Could not get parameters for content object with id ',
                this.playerModel.contentId,
                '. The copyright text might be incomplete. Details: ',
                error
            );
        }

        return this.h5pObject.getCopyrights(
            this.h5pInstance,
            parameters,
            this.playerModel.contentId,
            metadata
        );
    }

    /**
     * @returns true if there is copyright information to be displayed.
     */
    public hasCopyrightInformation(): boolean {
        return !!this.getCopyrightHtml();
    }

    /**
     * Asks the H5P content to resize itself inside the dimensions of the
     * container.
     *
     * Has no effect until the H5P object has fully initialized.
     */
    public resize(): void {
        if (!this.h5pInstance || !this.h5pInstance.trigger) {
            return;
        }
        this.h5pInstance.trigger('resize');
    }

    /**
     * Displays the copyright notice in the regular H5P way.
     */
    public showCopyright(): void {
        const copyrightHtml = this.getCopyrightHtml();
        const dialog = new this.h5pObject.Dialog(
            'copyrights',
            this.h5pObject.t('copyrightInformation'),
            copyrightHtml,
            this.h5pObject.jQuery('.h5p-container')
        );
        dialog.open(true);
    }

    /**
     * Attempts to find the H5P instance and emit the initialized event.
     * Used both by the externalDispatcher callback and the fallback poll.
     * Returns true if initialized was emitted.
     */
    private tryEmitInitialized(): boolean {
        if (this.initializedEmitted) return true;
        // Must reflect what was ACTUALLY rendered (see render(): div is preferred but
        // iframe is used when the model asks for it). Hardcoding true left
        // h5pObject/h5pWindow/h5pInstance pointing at the PARENT window for an
        // iframe-embedded content, so the instance was never found and every
        // instance-level feature (learner snapshot, grading) silently no-oped.
        const divMode = this.playerModel?.embedTypes?.includes('div') ?? true;
        this.h5pObject = divMode
            ? window.H5P
            : (
                  document.getElementById(
                      `h5p-iframe-${this.playerModel.contentId}`
                  ) as HTMLIFrameElement
              )?.contentWindow?.H5P;
        this.h5pWindow = divMode
            ? window
            : (
                  document.getElementById(
                      `h5p-iframe-${this.playerModel.contentId}`
                  ) as HTMLIFrameElement
              )?.contentWindow;
        this.h5pInstance = this.h5pObject?.instances?.find(
            // H5P converts our string contentId into number, so we don't use ===
             
            (i) => i.contentId == this.contentId
        );
        if (this.h5pInstance) {
            this.initializedEmitted = true;
            this.clearInitializedFallback();
            if (window.H5P?.externalDispatcher) {
                window.H5P.externalDispatcher.off('initialized', this.onContentInitialized);
            }
            this.dispatchEvent(
                new CustomEvent('initialized', {
                    detail: { contentId: this.contentId },
                })
            );
            return true;
        }
        return false;
    }

    private clearInitializedFallback(): void {
        if (this.initializedFallbackTimer) {
            clearInterval(this.initializedFallbackTimer);
            this.initializedFallbackTimer = null;
        }
    }

    /**
     * H5P core only copies `library.userDatas.state` into `extras.previousState` when
     * `H5PIntegration.saveFreq` is truthy. Embed strips hub URLs and sets `saveFreq`
     * to false, which blocks restore for content types (e.g. Blanks) that read
     * `extras.previousState` — even when state was preloaded via `contentUserData`.
     * Mirror the core assignment here so inline/session state still reaches the ctor.
     */
    private patchNewRunnableForEmbedUserState(h5p: IH5P): void {
        const marked = h5p as IH5P & { __aiHubNewRunnableEmbedStatePatched?: boolean };
        if (marked.__aiHubNewRunnableEmbedStatePatched || typeof h5p.newRunnable !== 'function') {
            return;
        }
        marked.__aiHubNewRunnableEmbedStatePatched = true;
        h5p.newRunnable = this.wrapNewRunnableForEmbedUserState(
            h5p.newRunnable as (...args: unknown[]) => unknown,
        );
    }

    /**
     * The `newRunnable` wrapper shared by the host-document patch above and the
     * isolated-iframe patch (`armIframeUserStatePatch`). Copies a preloaded
     * `library.userDatas.state` into `extras.previousState` — the assignment H5P
     * core only performs when `saveFreq` is truthy — so a resumed draft reaches the
     * content constructor regardless of which realm's `newRunnable` boots it.
     */
    private wrapNewRunnableForEmbedUserState(
        original: (...args: unknown[]) => unknown,
    ): (...args: unknown[]) => unknown {
        return function (
            library: {
                userDatas?: { state?: unknown };
                library: string;
                params: unknown;
                metadata?: unknown;
                subContentId?: number;
            },
            contentId: string,
            $attachTo: unknown,
            skipResize: boolean,
            extras:
                | {
                      previousState?: unknown;
                      standalone?: boolean;
                      metadata?: unknown;
                      parent?: unknown;
                      subContentId?: number;
                  }
                | undefined
        ) {
            const ext = extras ?? {};
            if (!ext.previousState && library?.userDatas && library.userDatas.state) {
                ext.previousState = library.userDatas.state;
            }
            return original(library, contentId, $attachTo, skipResize, ext);
        };
    }

    /**
     * EAA-1929-h5p-resume-draft-overwrite: `patchNewRunnableForEmbedUserState`
     * only ever patches `window.H5P` of the HOST document (see the call site in
     * `render()`). In `forceIframeEmbed` mode the content actually boots inside
     * the isolated iframe's OWN realm — its own `window.H5P`, a DIFFERENT object
     * from the host's — so that patch never reaches the code path that copies a
     * resumed draft (`contentUserData`) into `extras.previousState`. The iframe
     * boots with no previous answer even though
     * `H5PIntegration.contents[cid].contentUserData` was set correctly (see
     * H5PPlayerUI.tsx), which is why a resumed fill-in-the-blank rendered empty
     * although the server held the real draft — confirmed live: the draft was
     * genuinely persisted server-side, but the isolated iframe's own H5P.Blanks
     * instance never received `extras.previousState`.
     *
     * DETERMINISTIC (EAA-1929 grade/hydrate follow-up): `armIframeUserStatePatch`
     * runs synchronously right after the `about:blank` iframe is appended — before
     * the iframe realm's own H5P core script has executed — so `contentWindow.H5P`
     * is still undefined at arm time. Rather than POLL for it (a 10ms timer that
     * can in principle lose the race against a fast child bootstrap), we install an
     * accessor on the iframe's `window.H5P`: its setter fires SYNCHRONOUSLY the
     * instant core does `window.H5P = window.H5P || {}`. Core attaches `newRunnable`
     * to that object only later in the same script, so the setter in turn installs
     * a second accessor on `H5P.newRunnable` that wraps the function the moment core
     * assigns it — still guaranteed before it can be invoked to construct content.
     * Same origin means this ordering is deterministic.
     *
     * A short polling fallback is kept for the cases the accessor cannot cover
     * (defineProperty refused, or core replacing the whole `H5P` object). Combined
     * with the flush-side guard in `EmbedMultiPlayerPage` (`captureH5pBaseline`,
     * which never persists a snapshot unless it differs from the one captured right
     * after load), a lost race can at worst leave the DISPLAY stale until the next
     * real edit — it can never overwrite the learner's saved draft.
     */
    private armIframeUserStatePatch(iframe: HTMLIFrameElement): void {
        const win = iframe.contentWindow as (Window & { H5P?: IH5P }) | null | undefined;
        if (!win) {
            return;
        }

        // Already booted (cached core / div reuse): patch straight away.
        if (win.H5P) {
            this.patchNewRunnableForEmbedUserState(win.H5P);
            return;
        }

        // Deterministic path: intercept the iframe realm's OWN assignment of
        // `window.H5P`.
        let accessorInstalled = false;
        try {
            let h5pValue: IH5P | undefined;
            Object.defineProperty(win, 'H5P', {
                configurable: true,
                enumerable: true,
                get: () => h5pValue,
                set: (value: IH5P) => {
                    // Store FIRST, patch second, and never let a patch failure
                    // propagate out of `window.H5P = ...` — the iframe realm's H5P
                    // bootstrap must always complete even if our hydrate patch
                    // throws (worst case falls back to the poll below / stale
                    // display, never a broken content load).
                    h5pValue = value;
                    if (value) {
                        try {
                            this.armNewRunnablePatchOnObject(value);
                        } catch {
                            /* fall back to the poll; display-only feature */
                        }
                    }
                },
            });
            accessorInstalled = true;
        } catch {
            /* property already defined non-configurable — rely on the poll below */
        }

        // Fallback poll. Shortened when the accessor is in place (it only has to
        // cover a whole-object replacement); full window otherwise.
        const pollIntervalMs = 10;
        const pollMaxMs = accessorInstalled ? 1000 : 5000;
        let elapsed = 0;
        this.iframeUserStatePatchTimer = setInterval(() => {
            elapsed += pollIntervalMs;
            const h5p = (iframe.contentWindow as (Window & { H5P?: IH5P }) | null)?.H5P;
            if (h5p) {
                this.patchNewRunnableForEmbedUserState(h5p);
            }
            const patched =
                !!h5p &&
                (h5p as { __aiHubNewRunnableEmbedStatePatched?: boolean })
                    .__aiHubNewRunnableEmbedStatePatched === true;
            const done = patched || elapsed >= pollMaxMs || !iframe.isConnected;
            if (done && this.iframeUserStatePatchTimer) {
                clearInterval(this.iframeUserStatePatchTimer);
                this.iframeUserStatePatchTimer = null;
            }
        }, pollIntervalMs);
    }

    /**
     * Ensure `patchNewRunnableForEmbedUserState`'s effect lands on `h5p` even when
     * core has not yet attached `newRunnable`. If it is already a function we wrap
     * it directly; otherwise we intercept the pending assignment with an accessor so
     * the wrap is applied the instant core assigns `H5P.newRunnable`, before it can
     * be called. Marking the object as patched keeps the fallback poll from
     * double-wrapping through `patchNewRunnableForEmbedUserState`.
     */
    private armNewRunnablePatchOnObject(h5p: IH5P): void {
        const marked = h5p as IH5P & { __aiHubNewRunnableEmbedStatePatched?: boolean };
        if (marked.__aiHubNewRunnableEmbedStatePatched) {
            return;
        }
        if (typeof h5p.newRunnable === 'function') {
            this.patchNewRunnableForEmbedUserState(h5p);
            return;
        }
        marked.__aiHubNewRunnableEmbedStatePatched = true;
        let runnable: ((...args: unknown[]) => unknown) | undefined;
        try {
            Object.defineProperty(h5p, 'newRunnable', {
                configurable: true,
                enumerable: true,
                get: () => runnable,
                set: (value: unknown) => {
                    // If wrapping throws, store the ORIGINAL function so core can
                    // still construct content (loses only the hydrate patch, never
                    // the content load itself).
                    if (typeof value === 'function') {
                        try {
                            runnable = this.wrapNewRunnableForEmbedUserState(
                                value as (...args: unknown[]) => unknown,
                            );
                        } catch {
                            runnable = value as (...args: unknown[]) => unknown;
                        }
                    } else {
                        runnable = value as ((...args: unknown[]) => unknown) | undefined;
                    }
                },
            });
        } catch {
            // Interception failed — undo the marker so the fallback poll retries.
            marked.__aiHubNewRunnableEmbedStatePatched = false;
        }
    }

    /**
     * Called when any H5P content signals that it was initialized.
     * Some content types may not fire this event; a fallback poll ensures we always emit.
     */
    private onContentInitialized = (): void => {
        this.tryEmitInitialized();
    };

    private onxAPI = (event: IxAPIEvent): void => {
        if (
            `${event.data?.statement?.object?.definition?.extensions['http://h5p.org/x-api/h5p-local-content-id']}` ===
            `${this.playerModel.contentId}`
        ) {
            const context: IContext = {
                contentId: this.playerModel.contentId,
            };
            this.dispatchEvent(
                new CustomEvent('xAPI', {
                    detail: {
                        statement: event.data.statement,
                        context,
                        event,
                    },
                })
            );
        }
    };

    /**
     * Displays content.
     * @param {string} contentId
     */
    private async render(
        contentId: string,
        contextId?: string,
        asUserId?: string,
        readOnlyState?: string
    ): Promise<void> {
        if (!this.loadContentCallback) {
            return;
        }
        const gen = ++this.renderGeneration;
        // Get data from H5P server (merged model from React includes session / embed state)
        try {
            this.playerModel = await this.loadContentCallback(
                contentId,
                contextId,
                asUserId,
                readOnlyState === 'true'
            );
        } catch (error: unknown) {
            if (gen !== this.renderGeneration) {
                return;
            }
            console.error('Error loading H5P content from server:', error);
            this.root.innerHTML = `
                <div style="
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 32px 24px;
                    text-align: center;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                ">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#bfbfbf" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom: 16px;">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <p style="margin: 0 0 8px; font-size: 15px; font-weight: 500; color: #434343;">
                        Unable to load content
                    </p>
                    <p style="margin: 0; font-size: 13px; color: #8c8c8c; max-width: 360px;">
                        This content could not be displayed. Please try refreshing the page or contact support if the problem persists.
                    </p>
                </div>`;
            return;
        }

        if (gen !== this.renderGeneration) {
            return;
        }

        this.initializedEmitted = false;
        this.clearInitializedFallback();

        // EAA-1929: from here to H5P.init() is the shared-global critical section.
        await withH5PBootstrapLock(async () => {

        // EAA-755: clear the DOM INSIDE the lock, immediately before the append.
        //
        // The reset used to sit ABOVE `withH5PBootstrapLock`, which let two
        // overlapping renders each clear an already-empty root and then BOTH append,
        // leaving two `.h5p-content` divs and two H5P instances for one contentId —
        // the content rendered twice, stacked.
        //
        //   A: gen=1 -> guard ok -> clear -> takes lock -> awaits addScripts
        //   B: gen=2 -> guard ok -> clear (root still empty) -> blocks on lock
        //   A: scripts resolve -> APPENDS #1 -> H5P.init -> releases
        //   B: takes lock -> APPENDS #2 -> H5P.init
        //
        // Clearing inside the lock makes clear+append atomic against another render,
        // so the last one to run owns the DOM. The generation re-check drops a render
        // that a newer one has already superseded before it touches anything.
        //
        // The race was always there; it became reachable when the static H5P manifest
        // path landed, because `loadContentCallback` now resolves from a local file
        // instead of a round trip to the H5P server, so two renders overlap easily.
        if (gen !== this.renderGeneration) {
            return;
        }
        this.root.innerHTML = '';

        // We have to prevent H5P from initializing when the h5p.js file is
        // loaded.
        if (!window.H5P) {
            window.H5P = {} as any;
        }
        window.H5P.preventInit = true;

        // We merge the H5P integration we received from the server with the one
        // that already exists in the window globally to allow for several H5P
        // content objects on a single page.
        mergeH5PIntegration(this.playerModel.integration, this.playerModel.contentId);

        // The server has already told us which embed types are generally
        // acceptable for the content type, but we prefer div if possible to
        // avoid too many iframes.
        if (this.playerModel.embedTypes.includes('div')) {
            await this.renderDiv(this.playerModel);
        } else {
            await this.renderIframe(this.playerModel);
        }

        if (gen !== this.renderGeneration) {
            return;
        }

        // Re-apply integration immediately before init so window.H5PIntegration
        // cannot drift during async script load vs a newer render.
        mergeH5PIntegration(this.playerModel.integration, this.playerModel.contentId);

        this.patchNewRunnableForEmbedUserState(window.H5P);

        // Initialize H5P with the component as root
        window.H5P.preventInit = false;
        if (window.H5P.externalDispatcher) {
            window.H5P.externalDispatcher.on('initialized', this.onContentInitialized, this);
        }
        window.H5P.preventInit = false;

        if (window.H5P.externalDispatcher) {
            // detach xAPI listener first to avoid having multiple listeners on the
            // same content (can safely be done even if it hasn't been attached
            // before)
            window.H5P.externalDispatcher.off('xAPI', this.onxAPI);
            // attach xAPI listener
            window.H5P.externalDispatcher.on('xAPI', this.onxAPI);
        }

        // EAA-755: drop instances whose DOM this render just discarded.
        //
        // `window.H5P.instances` is a page-global array that H5P only ever appends to.
        // A second render is normal here — `H5PPlayer`'s `loadContentCallback` is a
        // useCallback keyed on `content`, so its identity changes once the model
        // resolves — and each one calls `H5P.init`, so the array ends up holding the
        // superseded instance AND the live one for the same contentId. That matters
        // because `tryEmitInitialized()` resolves an instance with `.find()`, which
        // returns the FIRST match: the detached one. It would then emit `initialized`
        // against an instance bound to elements no longer in the document, so every
        // instance-level feature hanging off that event (learner snapshot, grading)
        // would be watching a dead tree.
        const liveInstances = window.H5P?.instances;
        if (Array.isArray(liveInstances)) {
            window.H5P.instances = liveInstances.filter((instance: any) => {
                const container = instance?.$container;
                const node = (container?.[0] ?? container) as Element | undefined;
                return node instanceof Element ? node.isConnected : true;
            });
        }

        window.H5P.init(this.root);
        });

        // Fallback: some H5P content types do not fire externalDispatcher
        // 'initialized'. Poll for our instance so we always emit initialized.
        const fallbackIntervalMs = 100;
        const fallbackMaxMs = 5000;
        let elapsed = 0;
        this.initializedFallbackTimer = setInterval(() => {
            elapsed += fallbackIntervalMs;
            if (this.tryEmitInitialized() || elapsed >= fallbackMaxMs) {
                this.clearInitializedFallback();
                return;
            }
        }, fallbackIntervalMs);
    }

    /**
     * Creates a new DOM for the H5P using a div as container.
     */
    private async renderDiv(playerModel: IPlayerModel): Promise<void> {
        addStylesheets(playerModel.styles, document.getElementsByTagName('head')[0]);
        await addScripts(playerModel.scripts, document.getElementsByTagName('head')[0]);

        // EAA-755: clear immediately before appending, AFTER the awaits above.
        //
        // The caller clears too, but it does so before `addScripts` resolves. Any
        // render that reaches this point with a non-empty root would otherwise stack a
        // second `.h5p-content` under the same component root — two copies of the
        // activity, two `H5P.instances` entries for one contentId. Clearing here makes
        // "one root, one content" hold no matter how the renders interleave, which is
        // the invariant H5P.init() depends on when it binds an element to a content.
        this.root.innerHTML = '';

        const h5pContainerDiv = document.createElement('div');
        h5pContainerDiv.className = 'h5p-iframe';
        this.root.appendChild(h5pContainerDiv);

        const h5pContentDiv = document.createElement('div');
        h5pContentDiv.className = 'h5p-content';
        h5pContentDiv.dataset.contentId = playerModel.contentId;
        h5pContainerDiv.appendChild(h5pContentDiv);
    }

    /**
     * Creates a new DOM for the H5P using an iframe as container.
     * @param {IPlayerModel} playerModel
     */
    private async renderIframe(playerModel: IPlayerModel): Promise<void> {
        // We don't need to load styles, as they are all loaded within the
        // iframe.
        await addScripts(
            window.H5PIntegration.core.scripts,
            document.getElementsByTagName('head')[0]
        );

        // EAA-755: same invariant as renderDiv — clear after the await, before the
        // append, so an overlapping render cannot leave two iframes stacked.
        this.root.innerHTML = '';

        const h5pIFrameWrapper = document.createElement('div');
        h5pIFrameWrapper.className = `h5p-iframe-wrapper`;
        h5pIFrameWrapper.innerHTML = `<iframe id="h5p-iframe-${playerModel.contentId}"
        class="h5p-iframe" data-content-id="${playerModel.contentId}"
        style="height:1px" src="about:blank" frameBorder="0" scrolling="no"
        title="H5P"></iframe>`;
        this.root.appendChild(h5pIFrameWrapper);

        // EAA-1929: keep the iframe as tall as its content.
        //
        // H5P ships `style="height:1px"` and relies on its own resize plumbing to grow
        // the frame. That plumbing is built for EXTERNAL embeds (h5p-resizer.js posting
        // to a foreign parent); for a same-origin iframe rendered by this component
        // nothing ever set a height, so the host showed an empty box — verified in the
        // LMS, where the whole attempt area was blank.
        //
        // The child is same-origin (about:blank filled in by H5P core), so its document
        // can be measured directly. ResizeObserver covers content that grows later
        // (feedback shown after a check, an image finishing load).
        const iframe = h5pIFrameWrapper.querySelector('iframe');
        if (iframe) {
            this.syncIframeHeight(iframe as HTMLIFrameElement);
            this.syncIframeTheme(iframe as HTMLIFrameElement);
            // EAA-1929-h5p-resume-draft-overwrite: patch THIS iframe's own H5P
            // realm so a resumed draft's contentUserData reaches the content
            // instance — see armIframeUserStatePatch.
            this.armIframeUserStatePatch(iframe as HTMLIFrameElement);
        }
    }

    /**
     * EAA-755: mirror the host's theme into a same-origin H5P iframe.
     *
     * `overrides/ach-player.css` is written for DIV embedding, and says so: it aliases
     * every colour off the host's tokens (`--achp-card: var(--card, ...)`) precisely
     * BECAUSE content normally renders in the host document, where those tokens and
     * `data-theme` already exist. All 58 manifests declare `embedTypes: ["div"]`, so
     * that held — until a player model resolved through the H5P server instead, which
     * asks for iframe embedding. Then the sheet loads inside a fresh `about:blank`
     * document that has neither the tokens nor `data-theme`, every alias falls back to
     * its LIGHT literal, and the content paints white inside a dark player.
     *
     * Two things are copied, and both are needed. `data-theme` (plus the Tailwind
     * `dark` class) is what selects the sheet's own dark ramp; the token block carries
     * a TENANT's palette across, which no literal fallback can reproduce.
     *
     * Best-effort by construction: a cross-origin child throws on `contentDocument` and
     * is skipped, exactly as `syncIframeHeight` does.
     */
    private syncIframeTheme(iframe: HTMLIFrameElement): void {
        const hostRoot = document.documentElement;

        const apply = (): void => {
            let doc: Document | null | undefined;
            try {
                doc = iframe.contentDocument;
            } catch {
                return; // cross-origin: nothing we can reach
            }
            const frameRoot = doc?.documentElement;
            const head = doc?.head;
            if (!frameRoot || !head) {
                return;
            }

            const mode = hostRoot.getAttribute('data-theme');
            if (mode) {
                frameRoot.setAttribute('data-theme', mode);
            }
            frameRoot.classList.toggle(
                'dark',
                mode === 'dark' || hostRoot.classList.contains('dark'),
            );

            const computed = getComputedStyle(hostRoot);
            const declarations = H5PPlayerComponent.THEME_TOKENS.map((name) => {
                const value = computed.getPropertyValue(name).trim();
                return value ? `${name}:${value};` : '';
            }).join('');
            if (!declarations) {
                return;
            }
            let style = doc?.getElementById(
                'ach-h5p-theme-tokens',
            ) as HTMLStyleElement | null;
            if (!style) {
                style = doc!.createElement('style');
                style.id = 'ach-h5p-theme-tokens';
                head.appendChild(style);
            }
            const block = `:root{${declarations}}`;
            if (style.textContent !== block) {
                style.textContent = block;
            }
        };

        iframe.addEventListener('load', apply);
        apply();

        // Re-apply when the HOST theme changes, so a light/dark toggle reaches content
        // that is already rendered instead of waiting for a reload.
        this.iframeThemeObserver?.disconnect();
        this.iframeThemeObserver = new MutationObserver(() => apply());
        this.iframeThemeObserver.observe(hostRoot, {
            attributes: true,
            attributeFilter: ['data-theme', 'class'],
        });

        // H5P fills the document after `about:blank` loads, and there is no event for
        // "core finished writing the head", so re-stamp briefly — same shape and budget
        // as the height sync above.
        let ticks = 0;
        const timer = setInterval(() => {
            ticks += 1;
            apply();
            if (ticks > 40) {
                clearInterval(timer);
            }
        }, 250);
        this.iframeThemeTimer = timer;
    }

    /** Observes a same-origin H5P iframe and mirrors its content height (EAA-1929). */
    private syncIframeHeight(iframe: HTMLIFrameElement): void {
        const apply = (): void => {
            let doc: Document | null | undefined;
            try {
                doc = iframe.contentDocument;
            } catch {
                return; // cross-origin: H5P's own resizer is the only option
            }
            const body = doc?.body;
            if (!body) {
                return;
            }
            const height = Math.max(
                body.scrollHeight,
                doc?.documentElement?.scrollHeight ?? 0,
            );
            if (height > 1) {
                iframe.style.height = `${height}px`;
            }
        };

        const observe = (): void => {
            apply();
            try {
                const win = iframe.contentWindow;
                const body = iframe.contentDocument?.body;
                if (!win || !body) {
                    return;
                }
                this.iframeHeightObserver?.disconnect();
                const ResizeObserverCtor = (win as unknown as {
                    ResizeObserver?: typeof ResizeObserver;
                }).ResizeObserver;
                if (ResizeObserverCtor) {
                    this.iframeHeightObserver = new ResizeObserverCtor(() => apply());
                    this.iframeHeightObserver.observe(body);
                }
            } catch {
                /* measuring is best effort; the poll below still runs */
            }
        };

        iframe.addEventListener('load', observe);
        observe();
        // H5P fills the document after `about:blank` loads, and some content types grow
        // once media settles, so re-measure for a short while regardless of events.
        let ticks = 0;
        const timer = setInterval(() => {
            ticks += 1;
            apply();
            if (ticks > 40) {
                clearInterval(timer);
            }
        }, 250);
        this.iframeHeightTimer = timer;
    }
}
